import { FC, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { capitalize } from 'lodash';
import { formatDistanceToNow } from 'date-fns';
import { List, MessageMedium } from 'modules/graphql/types';

import {
  postMessageToSelf,
  postMessageToInternal,
  fetchEmailLists,
  postMessageAsRandom,
  postMessageToAddress,
  postMessageToUserId,
  putDisplayTestMessageToInternal,
  deleteDisplayTestMessageToInternal,
  deleteDisplayTestMessageToSelf,
  putDisplayTestMessageToSelf,
  deleteDisplayTestMessageToAddresses,
  putDisplayTestMessageToAddresses,
} from 'api/TemplateEditAPI';
import { ListType } from 'modules/domains';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { selectActingUser } from 'core/userContext';

import { useQueryParamContext } from './QueryParamContext';
import {
  ListOption,
  SendTestMsgContext,
  SendTestMsgContextState,
  TestMsgModals,
  UserData,
} from './SendTestMsgContext';
import {
  displayContent,
  getListLabelFromOptions,
  SendTestMessageErrorTypes,
  SendTemplateTypes,
} from '../common';
import { useEditableFieldsContext } from './EditableFieldsContext';
import { useTemplateQueryContext } from './TemplateQueryContext';
import { useCampaignQueryContext } from './CampaignQueryContext';
import { useDisplayTestMessageStatus } from '../hooks/useDisplayTestMessageStatus';

export const formatListOptions = (listData: RestListType[]) => {
  return listData.map((listItem) => {
    const convertedDate = formatDistanceToNow(new Date(listItem.createdAt), {
      addSuffix: true,
    });
    const bulletOperator = '\u{2219}';
    return {
      doubleLine: `Created: ${convertedDate} ${bulletOperator} ID: ${listItem.id}`,
      id: listItem.id.toString(),
      label: listItem.name,
      value: listItem.id,
    };
  });
};

const errorLookup: Record<SendTestMessageErrorTypes, string> = {
  [SendTestMessageErrorTypes.NO_RECIPIENTS_FOUND]: 'No recipients found to send proof',
  [SendTestMessageErrorTypes.SMS_MISSING_FIELDS]: 'missing required fields phoneNumber',
  [SendTestMessageErrorTypes.WEB_PUSH_MISSING_FIELDS]: 'missing required field browserTokens',
  [SendTestMessageErrorTypes.PUSH_MISSING_FIELDS]: 'missing required field devices',
};

const getError = (errorMessage = '') => {
  const matchingError: SendTestMessageErrorTypes = (
    Object.keys(errorLookup) as SendTestMessageErrorTypes[]
  ).filter((errorKey) =>
    errorMessage.includes(errorLookup[errorKey]),
  )[0] as SendTestMessageErrorTypes;
  return errorLookup[matchingError];
};

const removeKey = (obj: Record<string, string>, keyToRemove: string) => {
  const newObj = { ...obj };
  delete newObj[keyToRemove];
  return newObj;
};

type RestListType = Omit<List, 'listType'> & { emailListType: ListType };

type OriginalListsResponse = {
  emailLists: RestListType[];
  internalLists: RestListType[];
};

type NewListsResponse = {
  emailLists: RestListType[];
};

export const SendTestMsgContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useDispatch();
  const { locale, templateId } = useQueryParamContext();
  const { campaign } = useCampaignQueryContext();
  const actingUser = useSelector(selectActingUser);
  const [sendErrors, setSendErrors] = useState<Record<string, string>>({});
  const { liveTemplate } = useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();
  const { refetch: refetchDisplayTestMessageStatus } = useDisplayTestMessageStatus();
  const [emailListOptions, setEmailListOptions] = useState<ListOption[]>([]);
  const [internalListOptions, setInternalListOptions] = useState<ListOption[]>([]);
  const [isFetchingLists, setIsFetchingLists] = useState<boolean>(false);
  const [isSendingTestMsg, setIsSendingTestMsg] = useState<boolean>(false);
  const [openTestMsgModal, setOpenTestMsgModal] = useState<TestMsgModals>(TestMsgModals.None);
  const [sendTestAsRandomUserData, setSendTestAsRandomUserData] = useState<UserData[]>([]);

  const sendTemplateDefault =
    medium === MessageMedium.Email ? SendTemplateTypes.PROOF : SendTemplateTypes.TEST_MESSAGE;

  const toastName = campaign.name ?? liveTemplate.name;

  const onFetchLists = useCallback(() => {
    // Safeguard, handles if response for fetch email list contains separated out internal lists (should be deprecated)
    const handleOriginalResponse = (data: OriginalListsResponse) => {
      const { emailLists, internalLists } = data;
      setEmailListOptions(formatListOptions(emailLists));
      setInternalListOptions(formatListOptions(internalLists));
    };
    // Handles updated fetch email list endpoint which no longers filters internal lists on BE
    const handleNewResponse = (data: NewListsResponse) => {
      setEmailListOptions(formatListOptions(data.emailLists));

      setInternalListOptions(
        formatListOptions(
          data.emailLists.filter((list) => list.emailListType === ListType.Internal),
        ),
      );
    };

    setIsFetchingLists(true);
    fetchEmailLists()
      .then(({ data }) => {
        if (data.internalLists) {
          handleOriginalResponse(data);
        } else {
          handleNewResponse(data);
        }
      })
      .catch((error: any) => {
        dispatch(addNegativeToast(displayContent.errTestMessageList, error));
      })
      .finally(() => {
        setIsFetchingLists(false);
      });
  }, [templateId]);

  const onSendTestAsRandom = useCallback(
    (selectedListId: number, recipient: string, testSize: number, uuidOrEmail: string) => {
      const onSendTestAsRandomKey = 'onSendTestAsRandom';
      const sendSubject = toastName ?? sendTemplateDefault;
      const listName = getListLabelFromOptions(emailListOptions, selectedListId) ?? 'user list';
      setIsSendingTestMsg(true);
      postMessageAsRandom(selectedListId, templateId, recipient, testSize)
        .then((results) => {
          setSendTestAsRandomUserData(results.data);
          setSendErrors((errors) => removeKey(errors, onSendTestAsRandomKey));
          dispatch(
            addPositiveToast(
              capitalize(
                displayContent.strSendTemplateSuccessAsRandom({
                  sendSubject,
                  uuidOrEmail,
                  listName,
                }),
              ),
            ),
          );
        })
        .catch((error: any) => {
          const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
          setSendErrors((errors) => ({ ...errors, [onSendTestAsRandomKey]: errorResponse }));
          dispatch(
            addNegativeToast(
              displayContent.errTestMessage({
                recipient: uuidOrEmail,
                sendSubject,
                error: errorResponse,
              }),
            ),
          );
        })
        .finally(() => {
          setIsSendingTestMsg(false);
        });
    },
    [templateId],
  );

  const onSendTestToInternalList = useCallback(
    (selectedListId: number) => {
      const onSendTestToInternalListKey = 'onSendTestToInternalList';
      const sendSubject = toastName ?? sendTemplateDefault;
      const listName = getListLabelFromOptions(emailListOptions, selectedListId) ?? 'internal list';
      setIsSendingTestMsg(true);
      setSendErrors((errors) => removeKey(errors, onSendTestToInternalListKey));
      postMessageToInternal(selectedListId, templateId, locale)
        .then(() => {
          dispatch(
            addPositiveToast(
              capitalize(
                displayContent.strSendTemplateSuccess({
                  sendSubject,
                  recipient: listName,
                }),
              ),
            ),
          );
        })
        .catch((error: any) => {
          const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
          setSendErrors((errors) => ({ ...errors, [onSendTestToInternalListKey]: errorResponse }));
          dispatch(
            addNegativeToast(
              displayContent.errTestMessage({
                recipient: listName,
                sendSubject,
                error: errorResponse,
              }),
            ),
          );
        })
        .finally(() => {
          setIsSendingTestMsg(false);
        });
    },
    [locale, templateId],
  );

  const onSendTestToOtherEmail = useCallback(
    (emails: string[]) => {
      const onSendTestToOtherEmailKey = 'onSendTestToOtherEmail';
      const sendSubject = toastName ?? sendTemplateDefault;
      const emailsCsv = emails.map(encodeURIComponent).join(',');
      const displayText = emails.join(', ');
      setIsSendingTestMsg(true);
      setSendErrors((errors) => removeKey(errors, onSendTestToOtherEmailKey));
      postMessageToAddress(templateId, emailsCsv, locale)
        .then(() => {
          dispatch(
            addPositiveToast(
              displayContent.strSendTemplateSuccess({
                sendSubject,
                recipient: displayText,
              }),
            ),
          );
        })
        .catch((error: any) => {
          const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
          setSendErrors((errors) => ({ ...errors, [onSendTestToOtherEmailKey]: errorResponse }));
          dispatch(
            addNegativeToast(
              displayContent.errTestMessage({
                recipient: displayText,
                sendSubject,
                error: errorResponse,
              }),
            ),
          );
        })
        .finally(() => {
          setIsSendingTestMsg(false);
        });
    },
    [locale, templateId],
  );

  const onSendTestToOtherUserId = useCallback(
    (userIds: string[]) => {
      const onSendTestToOtherUserIdKey = 'onSendTestToOtherUserId';
      const sendSubject = toastName ?? sendTemplateDefault;
      const userIdCsv = userIds.map(encodeURIComponent).join(',');
      const displayText = userIds.join(', ');
      setIsSendingTestMsg(true);
      postMessageToUserId(templateId, userIdCsv, locale)
        .then(() => {
          dispatch(
            addPositiveToast(
              displayContent.strSendTemplateSuccess({
                sendSubject,
                recipient: displayText,
              }),
            ),
          );
        })
        .catch((error: any) => {
          const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
          setSendErrors((errors) => ({ ...errors, [onSendTestToOtherUserIdKey]: errorResponse }));
          dispatch(
            addNegativeToast(
              displayContent.errTestMessage({
                recipient: displayText,
                sendSubject,
                error: errorResponse,
              }),
            ),
          );
        })
        .finally(() => {
          setIsSendingTestMsg(false);
        });
    },
    [locale, templateId],
  );

  const onSendTestToSelf = useCallback(() => {
    const onSendToSelfKey = 'onSendToSelf';
    const recipientDisplayString =
      actingUser ?? liveTemplate.creatorUser?.id ?? liveTemplate.creatorUser?.fullName ?? 'myself';
    const sendSubject = toastName ?? sendTemplateDefault;
    setIsSendingTestMsg(true);
    postMessageToSelf(templateId, locale)
      .then(() => {
        dispatch(
          addPositiveToast(
            displayContent.strSendTemplateSuccess({
              sendSubject,
              recipient: recipientDisplayString,
            }),
          ),
        );
      })
      .catch((error: any) => {
        const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
        setSendErrors((errors) => ({ ...errors, [onSendToSelfKey]: errorResponse }));
        dispatch(
          addNegativeToast(
            displayContent.errTestMessage({
              recipient: recipientDisplayString,
              sendSubject,
              error: errorResponse,
            }),
          ),
        );
      })
      .finally(() => {
        setIsSendingTestMsg(false);
      });
  }, [locale, templateId, toastName]);

  const handleDisplayTestMessageSuccess = (
    removing: boolean,
    errorKey: string,
    recipientDisplayString: string,
  ) => {
    setSendErrors((errors) => removeKey(errors, errorKey));
    dispatch(
      addPositiveToast(
        `Test message ${removing ? 'removed' : 'displaying'} for ${recipientDisplayString}`,
      ),
    );
  };

  const handleDisplayTestMessageError = (
    error: any,
    removing: boolean,
    errorKey: string,
    recipientDisplayString: string,
  ) => {
    const errorResponse = getError(error?.response?.data) ?? displayContent.defaultError;
    setSendErrors((errors) => ({ ...errors, [errorKey]: errorResponse }));
    dispatch(
      addNegativeToast(
        `Unable to ${removing ? 'remove' : 'display'} test message for ${recipientDisplayString}`,
      ),
    );
  };

  const onDisplayTestToSelf = (removingDisplayMessage = false) => {
    const onDisplayTestToSelfKey = 'onDisplayTestToSelf';
    const selfEmail = actingUser;

    if (!selfEmail) {
      dispatch(
        addNegativeToast(`Unable to ${removingDisplayMessage ? 'remove' : 'display'} test message`),
      );
      return;
    }

    if (removingDisplayMessage) {
      deleteDisplayTestMessageToSelf(selfEmail, templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToSelfKey,
            selfEmail,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToSelfKey,
            selfEmail,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    } else {
      putDisplayTestMessageToSelf(selfEmail, templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToSelfKey,
            selfEmail,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToSelfKey,
            selfEmail,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    }
  };

  const onDisplayTestToInternalList = (selectedListId: number, removingDisplayMessage = false) => {
    const onDisplayTestToInternalListKey = 'onDisplayTestToInternalList';
    const recipientDisplay =
      getListLabelFromOptions(emailListOptions, selectedListId) ?? 'internal list';

    if (removingDisplayMessage) {
      deleteDisplayTestMessageToInternal(selectedListId, templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToInternalListKey,
            recipientDisplay,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToInternalListKey,
            recipientDisplay,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    } else {
      putDisplayTestMessageToInternal(selectedListId, templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToInternalListKey,
            recipientDisplay,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToInternalListKey,
            recipientDisplay,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    }
  };

  const onDisplayTestToOtherEmail = (emails: string[], removingDisplayMessage = false) => {
    const onDisplayTestToAddressesKey = 'onDisplayTestToAddresses';
    const recipientDisplay = emails.join(', ');

    if (removingDisplayMessage) {
      deleteDisplayTestMessageToAddresses(emails.join(','), templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToAddressesKey,
            recipientDisplay,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToAddressesKey,
            recipientDisplay,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    } else {
      putDisplayTestMessageToAddresses(emails.join(','), templateId)
        .then(() =>
          handleDisplayTestMessageSuccess(
            removingDisplayMessage,
            onDisplayTestToAddressesKey,
            recipientDisplay,
          ),
        )
        .catch((e) =>
          handleDisplayTestMessageError(
            e,
            removingDisplayMessage,
            onDisplayTestToAddressesKey,
            recipientDisplay,
          ),
        )
        .finally(refetchDisplayTestMessageStatus);
    }
  };

  const context: SendTestMsgContextState = {
    emailListOptions,
    internalListOptions,
    isFetchingLists,
    isSendingTestMsg,
    onFetchLists,
    onSendTestAsRandom,
    onSendTestToInternalList,
    onSendTestToOtherEmail,
    onSendTestToOtherUserId,
    onSendTestToSelf,
    onDisplayTestToInternalList,
    onDisplayTestToOtherEmail,
    onDisplayTestToSelf,
    openTestMsgModal,
    sendTestAsRandomUserData,
    setOpenTestMsgModal,
    sendErrors,
  };

  return <SendTestMsgContext.Provider value={context}>{children}</SendTestMsgContext.Provider>;
};
